import {inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {VehicleHistoryRepo} from "../api/vehicle/vehicle-history.repo";
import {DuckDbHelper, sqlDirection, sqlNumber, sqlSelectedIn, sqlText, sqlVehicleFilter} from "./duckdb.helper";
import {OrgDto} from "../dto/org.dto";
import {WrapperDto} from "../dto/wrapper.dto";
import {VehicleHistoryDto} from "../dto/vehicle/vehicle-history.dto";
import {SearchVehicleHistoryRes} from "../dto/vehicle/search-vehicle-history.res";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";
import {LoggingService} from "../../services/logger/logging.service";
import {isDefined} from "../../libs/utils/epic.utils";

@Injectable({providedIn: 'root'})
export class VehicleHistoryDuckDb extends VehicleHistoryRepo {

  private readonly duck: DuckDbHelper = inject(DuckDbHelper);
  private readonly log: LoggingService = inject(LoggingService);

  public override getById(_org: OrgDto, id: string, vehicleId: string): Observable<WrapperDto<VehicleHistoryDto>> {
    const select = `
      SELECT h.id         AS id,
             h.type       AS type,
             h.recorded   AS recorded,
             h.odometer   AS odometer,
             h.notes      AS notes,
             h.invoice_id AS invoiceId,
             h.vehicle_id AS vehicleId
      FROM vehicle_history h
      WHERE h.id = ${sqlText(id)}
        AND h.vehicle_id = ${sqlText(vehicleId)}`;
    return this.duck.sql(select).pipe(map((res: any) =>
      DuckDbHelper.data('VehicleHistoryDto', new VehicleHistoryDto(DuckDbHelper.firstRes(res)))));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchVehicleHistoryRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const filter = this.buildFilter(request);
    const search = `
      SELECT h.id         AS id,
             h.type       AS type,
             h.recorded   AS recorded,
             h.odometer   AS odometer,
             h.notes      AS notes,
             h.invoice_id AS invoiceId,
             h.vehicle_id AS vehicleId,
             COUNT(*)        OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING) AS total_results
      FROM vehicle_history h
      WHERE ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res: any) => {
      const rows: any[] = DuckDbHelper.hasRes(res) ? res : [];
      const result = new SearchVehicleHistoryRes({
        items: rows,
        hits: rows.length > 0 ? rows[0].total_results : 0,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchVehicleHistoryRes', result);
    }));
  }

  private buildSort(request: SearchRequest): string {
    let field;
    switch (request.sortField) {
      case 'recorded':
        field = 'h.recorded';
        break;
      case 'type':
        field = 'h.type';
        break;
      case 'odometer':
        field = 'h.odometer';
        break;
      case 'notes':
        field = 'h.notes';
        break;
      default:
        this.log.warn('Unhandled sort field "{}"', [request.sortField]);
        field = 'h.recorded';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildFilter(request: SearchRequest): string {
    return [sqlVehicleFilter(request, 'h.vehicle_id'), sqlSelectedIn(request, 'type', 'h.type')]
      .filter((f) => isDefined(f))
      .join(' AND ');
  }
}
